<template>
  <div class="app-topics-header-spacer lg:hidden"></div>
  <div
    class="app-topics-header fixed top-0 right-0 left-0 z-10 mx-auto max-w-[440px] overflow-hidden bg-white py-4 transition-all duration-300 ease-in-out lg:sticky lg:max-w-none"
    :class="{ 'app-topics-header--scrolled': isScrolled }"
  >
    <div class="app-topics-header__logo">
      <svg
        class="h-auto w-full"
        width="481"
        height="178"
        viewBox="0 0 481 178"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M261.466 29.2786C279.471 29.2786 291.011 45.8572 291.011 86.3773C291.011 126.898 279.471 143.476 261.466 143.476C253.385 143.476 246.693 139.586 241.845 130.377L242.077 177.043H216.916V30.9134H241.845V42.3773C246.693 33.1686 253.385 29.2787 261.466 29.2786ZM254.077 47.6966C246.002 47.6966 241.845 58.3408 241.845 86.5824C241.845 114.824 245.996 125.264 254.077 125.264C261.925 125.264 266.082 115.439 266.082 86.5824C266.082 57.7261 262.152 47.6969 254.077 47.6966Z"
          fill="#020A0A"
        />
        <path d="M23.3125 143.694H0L36.8184 10.8792H59.9043L23.3125 143.694Z" fill="#020A0A" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M170.049 29.0736C194.286 29.0736 206.751 46.2673 206.751 86.3773C206.751 126.488 194.286 143.681 170.049 143.681C145.812 143.681 133.348 126.487 133.348 86.3773C133.348 46.2675 145.812 29.0738 170.049 29.0736ZM170.049 47.4915C162.201 47.4919 158.044 57.7262 158.044 86.3773C158.044 115.029 162.201 125.059 170.049 125.059C177.897 125.059 181.822 115.029 181.822 86.3773C181.822 57.7256 177.897 47.4915 170.049 47.4915Z"
          fill="#020A0A"
        />
        <path
          d="M372.494 29.0736C392.348 29.0736 404.812 41.3524 406.429 74.9193H383.575C382.651 55.0662 378.959 47.6966 372.727 47.6966C365.576 47.6969 361.187 57.316 361.187 86.3773C361.187 115.439 365.337 125.059 372.727 125.059C379.191 125.059 383.116 118.099 383.808 94.5609H406.661C405.504 130.787 393.04 143.681 372.494 143.681C349.641 143.681 336.484 128.128 336.484 86.3773C336.484 44.627 349.873 29.0736 372.494 29.0736Z"
          fill="#020A0A"
        />
        <path
          d="M445.664 29.0677C465.977 29.0678 478.907 41.5515 479.599 69.1839H458.128C457.436 52.1958 452.355 46.6713 446.123 46.6712C440.815 46.6712 437.815 50.5613 437.815 56.7005C437.815 66.5245 445.663 71.6452 454.669 77.1693L454.663 77.1566C466.668 84.7311 480.749 92.915 480.749 113.793C480.749 131.192 468.278 143.675 448.89 143.675C427.885 143.675 414.264 130.987 413.805 101.925H435.733C436.425 120.143 441.274 126.077 448.431 126.077C454.204 126.077 457.436 122.188 457.437 115.844C457.437 104.38 447.972 99.0604 437.815 92.9212C426.502 85.7628 414.73 77.7781 414.729 58.7454C414.729 40.9368 426.502 29.0677 445.664 29.0677Z"
          fill="#020A0A"
        />
        <path
          d="M112.111 30.9193H128.732V49.1322H112.111V117.285C112.111 121.789 114.42 123.629 119.96 123.629H128.732V141.842H109.345C94.5722 141.842 87.1826 135.087 87.1826 121.989V49.1263H72.8691V30.9134H87.1826V10.8548H112.111V30.9193Z"
          fill="#020A0A"
        />
        <path d="M326.327 141.841H301.166V30.9124H326.327V141.841Z" fill="#020A0A" />
        <path
          d="M310.463 0.33626C317.268 -1.24569 324.086 2.90543 325.692 9.60872C327.299 16.3122 323.084 23.0293 316.279 24.6117C309.474 26.1938 302.655 22.0426 301.049 15.3392C299.443 8.63556 303.658 1.91846 310.463 0.33626Z"
          fill="#020A0A"
        />
      </svg>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useWindowScroll } from '@vueuse/core';

  const { y: scrollY } = useWindowScroll();

  const isScrolled = computed(() => scrollY.value > 100);
</script>

<style lang="pcss">
  :root {
    --app-topics-header-expanded-height: 210px;
    --app-topics-header-collapsed-height: 68px;
  }

  .app-topics-header-spacer {
    height: var(--app-topics-header-expanded-height);
  }

  .app-topics-header {
    height: var(--app-topics-header-expanded-height);
  }

  /**
   * Anchor targets are siblings of the header and have to come to rest below it.
   *
   * The offset is the collapsed height, because that is what covers the page once it
   * is scrolled. On desktop the header is sticky, so it also takes up space in the
   * flow: a jump that starts while the header is still expanded has to absorb the
   * height it loses on the way down as well, and expanded minus collapsed is exactly
   * that. Which is why the expanded height covers both.
   */
  .app-topics-header ~ * {
    scroll-margin-top: var(--app-topics-header-collapsed-height);
  }

  @media (--screen-lg) {
    .app-topics-header:not(.app-topics-header--scrolled) ~ * {
      scroll-margin-top: var(--app-topics-header-expanded-height);
    }
  }

  .app-topics-header__logo {
    transform-origin: top left;
    transform: translateY(24px) translateX(-3%) scale(1);
    width: 106%;
    will-change: transform;
    transition: transform 0.3s ease-in-out;
  }

  .app-topics-header--scrolled {
    height: var(--app-topics-header-collapsed-height);

    .app-topics-header__logo {
      transform: translateY(0) translateX(24px) scale(0.2);
    }
  }

  @media (--screen-lg) {
    .app-topics-header {
      margin-right: calc(var(--spacing-9) * -1);
      padding-left: var(--spacing-6);
    }

    .app-topics-header__logo {
      transform: translateY(24px);
      width: 480px;
    }

    .app-topics-header--scrolled .app-topics-header__logo {
      transform: translateY(0) scale(0.2);
    }
  }
</style>
